import api from '../services/api';

/**
 * External Lookup Data Model
 * Handles all API calls for EDAMS lookup tables
 * 
 * These are reference data tables used for filters, dropdowns, and data enrichment:
 * - OM Statuses (job statuses)
 * - Priorities
 * - Application Types
 * - Stations/Depots
 * - Job Card Outcomes
 * - Job Card Notes
 * - Outcome Types
 * - Source Types
 */

// ============================================================================
// Individual Lookup Endpoints
// ============================================================================

/**
 * Get all OM Status values with counts
 */
export const getOmStatuses = async () => {
  try {
    const response = await api.get('/api/external/lookup/om-statuses');
    return response.data;
  } catch (error) {
    console.error('Error fetching OM statuses:', error);
    throw new Error('Failed to fetch OM statuses: ' + error.message);
  }
};

/**
 * Get all Job Card Status values with counts
 */
export const getJcStatuses = async () => {
  try {
    const response = await api.get('/api/external/lookup/jc-statuses');
    return response.data;
  } catch (error) {
    console.error('Error fetching JC statuses:', error);
    throw new Error('Failed to fetch JC statuses: ' + error.message);
  }
};

/**
 * Get all priority levels
 */
export const getPriorities = async () => {
  try {
    const response = await api.get('/api/external/lookup/priorities');
    return response.data;
  } catch (error) {
    console.error('Error fetching priorities:', error);
    throw new Error('Failed to fetch priorities: ' + error.message);
  }
};

/**
 * Get all application/job types
 */
export const getApplicationTypes = async () => {
  try {
    const response = await api.get('/api/external/lookup/application-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching application types:', error);
    throw new Error('Failed to fetch application types: ' + error.message);
  }
};

/**
 * Get all job card outcomes
 */
export const getJobCardOutcomes = async () => {
  try {
    const response = await api.get('/api/external/lookup/jc-outcomes');
    return response.data;
  } catch (error) {
    console.error('Error fetching job card outcomes:', error);
    throw new Error('Failed to fetch job card outcomes: ' + error.message);
  }
};

/**
 * Get all job card note types
 */
export const getJobCardNotes = async () => {
  try {
    const response = await api.get('/api/external/lookup/jc-notes');
    return response.data;
  } catch (error) {
    console.error('Error fetching job card notes:', error);
    throw new Error('Failed to fetch job card notes: ' + error.message);
  }
};

/**
 * Get all stations/depots
 */
export const getStations = async () => {
  try {
    const response = await api.get('/api/external/lookup/stations');
    return response.data;
  } catch (error) {
    console.error('Error fetching stations:', error);
    throw new Error('Failed to fetch stations: ' + error.message);
  }
};

/**
 * Get all maintenance activities
 */
export const getMaintenanceActivities = async (limit = null) => {
  try {
    const params = {};
    if (limit) params.limit = limit;
    const response = await api.get('/api/external/lookup/maintenance-activities', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching maintenance activities:', error);
    throw new Error('Failed to fetch maintenance activities: ' + error.message);
  }
};

/**
 * Get all outcome types
 */
export const getOutcomeTypes = async () => {
  try {
    const response = await api.get('/api/external/lookup/outcome-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching outcome types:', error);
    throw new Error('Failed to fetch outcome types: ' + error.message);
  }
};

/**
 * Get all source types
 */
export const getSourceTypes = async () => {
  try {
    const response = await api.get('/api/external/lookup/source-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching source types:', error);
    throw new Error('Failed to fetch source types: ' + error.message);
  }
};

// ============================================================================
// Bulk Load Endpoint
// ============================================================================

/**
 * Get all lookup tables in a single request (efficient for app initialization)
 */
export const getAllLookups = async () => {
  try {
    const response = await api.get('/api/external/lookup/all');
    return response.data;
  } catch (error) {
    console.error('Error fetching all lookups:', error);
    throw new Error('Failed to fetch lookup data: ' + error.message);
  }
};

export default {
  getOmStatuses,
  getJcStatuses,
  getPriorities,
  getApplicationTypes,
  getJobCardOutcomes,
  getJobCardNotes,
  getStations,
  getMaintenanceActivities,
  getOutcomeTypes,
  getSourceTypes,
  getAllLookups
};
